import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, ArrowDownLeft, CheckCircle2, Clock, Search } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCurrency = (a) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(a || 0);

export default function TransferMasuk() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const token = localStorage.getItem("user_token");

  const fetchTransfers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/gmail/incoming-transfers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransfers(res.data.transfers || []);
    } catch (err) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/user/gmail/poll`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.connected === false) {
        toast.error("Gmail belum terhubung. Hubungi admin.");
      } else {
        toast.success(`Pindai selesai: ${res.data.processed || 0} baru, ${res.data.matched || 0} cocok.`);
      }
      await fetchTransfers();
    } catch (err) {
      toast.error("Gagal memindai email");
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
    const iv = setInterval(fetchTransfers, 20000);
    return () => clearInterval(iv);
  }, [fetchTransfers]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white dark:from-slate-900 dark:to-slate-800">
      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5">
          <Link to="/" className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 shadow flex items-center justify-center" data-testid="transfer-masuk-back">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </Link>
          <h1 className="text-lg font-heading font-bold text-slate-800 dark:text-white">Transfer Masuk</h1>
          <button onClick={fetchTransfers} className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 shadow flex items-center justify-center" data-testid="transfer-masuk-refresh">
            <RefreshCw className="w-5 h-5 text-sky-600" />
          </button>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
          Daftar transfer masuk yang terdeteksi otomatis dari notifikasi bank. Hanya untuk informasi (lihat saja).
        </p>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="w-full h-11 mb-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="transfer-masuk-scan"
        >
          {scanning ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          {scanning ? "Memindai..." : "Pindai Email Sekarang"}
        </button>

        {loading ? (
          <div className="text-center py-16"><div className="spinner w-8 h-8 border-sky-500 mx-auto" /></div>
        ) : transfers.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
            <ArrowDownLeft className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">Belum ada transfer masuk terdeteksi</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transfers.map((t) => (
              <div key={t.id} className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-100 dark:border-slate-700 flex items-center justify-between" data-testid="transfer-masuk-item">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 dark:text-white">{t.amount != null ? formatCurrency(t.amount) : "-"}</p>
                    <p className="text-xs text-slate-400">{t.received_at ? new Date(t.received_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }) : ""}</p>
                    {t.sender_name && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Atas nama: {t.sender_name}</p>}
                    {t.matched_customer_name && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Pelanggan: {t.matched_customer_name}</p>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {t.status === "matched" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Cocok</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><Clock className="w-3.5 h-3.5" /> Belum cocok</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
